import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, PhoneCall, ShieldCheck, HeartHandshake } from "lucide-react";
import { api } from "../lib/api";
import { iconFor } from "../lib/categories";
import SearchBar from "../components/SearchBar";
import { Button } from "../components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1688619101875-0528b48b68e7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

export default function Landing() {
  const [stats, setStats] = useState({ total: 0, by_category: {} });
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      <Hero />
      <section className="py-16 md:py-24 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">01 — Browse by service</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tighter">Every kind of small business.</h2>
            </div>
            <Link to="/search" className="text-sm font-semibold underline" data-testid="browse-all-link">Browse all →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="category-grid">
            {cats.map((c, i) => {
              const Icon = iconFor(c);
              return (
                <Link key={c} to={`/search?category=${encodeURIComponent(c)}`}
                  data-testid={`category-tile-${i}`}
                  className="group p-5 border border-zinc-200 rounded-lg bg-white hover:border-black transition-colors flex items-center gap-4">
                  <div className="w-11 h-11 rounded-md bg-zinc-100 group-hover:bg-[#FFCC00] flex items-center justify-center transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-display font-bold leading-tight">{c}</p>
                    <p className="text-xs text-zinc-500 font-mono">{stats.by_category[c] || 0} listed</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      <HowItWorks />
      <Charity />
      <BusinessCta />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative border-b border-zinc-200 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-200 text-[11px] font-mono uppercase tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFCC00]" /> UK small business directory
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.02] mt-6">
            Find a trusted<br />
            <span className="bg-[#FFCC00] px-2 -mx-1">local pro.</span>
          </h1>
          <p className="text-zinc-600 mt-6 max-w-xl text-base md:text-lg leading-relaxed">
            Builders, electricians, gas engineers, barbers, mortgage advisors and more —
            independent small businesses near you, with direct contact details and photos of their work.
          </p>
          <div className="mt-8 max-w-2xl"><SearchBar /></div>
        </div>
        <div className="relative hidden lg:block">
          <div className="absolute -inset-3 bg-[#FFCC00] rounded-lg rotate-2" />
          <img src={HERO_IMG} alt="Small business owner in her van" className="relative rounded-lg w-full h-[460px] object-cover border border-black" />
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { Icon: Search, t: "Search", d: "Pick a service and enter your town or postcode." },
    { Icon: ShieldCheck, t: "Compare", d: "See photos of their work, services offered and areas covered." },
    { Icon: PhoneCall, t: "Contact", d: "Call, email or send an enquiry straight to the business." },
  ];
  return (
    <section className="py-16 md:py-24 border-b border-zinc-200 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">02 — How it works</p>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mb-10">No middleman. No lead fees.</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {steps.map(({ Icon, t, d }, i) => (
            <div key={t} className="p-6 bg-white border border-zinc-200 rounded-lg">
              <p className="font-mono text-xs text-zinc-400 mb-4">0{i + 1}</p>
              <Icon className="w-6 h-6 mb-3" />
              <p className="font-display text-xl font-bold">{t}</p>
              <p className="text-sm text-zinc-600 mt-1">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Charity() {
  return (
    <section className="py-16 md:py-24 border-b border-zinc-200" data-testid="charity-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-[auto_1fr] gap-8 md:gap-14 items-center">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-7xl md:text-8xl font-bold tracking-tighter bg-[#FFCC00] px-3 leading-none" data-testid="charity-percent">10%</span>
        </div>
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
            <HeartHandshake className="w-3.5 h-3.5" /> Giving back
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tighter">10% of our revenue goes to charity.</h2>
          <p className="text-zinc-600 mt-4 max-w-2xl leading-relaxed">
            TradeFlow UK gives 10% of everything we earn to charity. Every business that lists with us
            and every customer who finds a local pro here helps support good causes.
          </p>
        </div>
      </div>
    </section>
  );
}

function BusinessCta() {
  return (
    <section className="py-16 md:py-20 bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center justify-between gap-8">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-300 mb-2">For small businesses</p>
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tighter">
            Get your business <span className="bg-[#FFCC00] text-black px-2 -mx-1">listed.</span>
          </h2>
          <p className="text-zinc-300 mt-4 max-w-xl">Create your listing in minutes, add photos of your work and receive enquiries straight to your inbox.</p>
        </div>
        <Link to="/register" data-testid="landing-list-business-btn">
          <Button className="bg-[#FFCC00] hover:bg-[#E6B800] text-black h-12 px-6 font-semibold">
            List your business <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </section>
  );
}
